import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Briefcase, CheckCircle2, ChevronLeft, Home, Info, Loader2, Phone, Plus, Search, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const AttorneyNetworkPage = () => {
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchState, setSearchState] = useState("");
  const [searchPractice, setSearchPractice] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    primary_state: "",
    bar_number: "",
    practice_areas: [],
    skill_sets: [],
    hourly_rate: 250,
    years_experience: 5,
    availability: "project_based",
    home_court: "",
    preferred_courts: "",
    federal_court_admitted: false,
    works_with_teams: true,
    collaboration_style: "either",
    malpractice_insurance: true,
    insurance_amount: 1000000
  });
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  
  const PRACTICE_AREAS = [
    "personal_injury", "employment", "contract", "insurance_bad_faith", 
    "medical_malpractice", "product_liability", "business_litigation",
    "real_estate", "family", "criminal_defense", "immigration", "bankruptcy"
  ];
  
  const SKILL_SETS = [
    "motion_practice", "deposition", "trial", "appellate", "negotiation",
    "mediation", "arbitration", "discovery", "research", "drafting"
  ];
  
  const STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC"
  ];
  
  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchState) params.append('state', searchState);
      if (searchPractice) params.append('practice_area', searchPractice);
      const res = await axios.get(`${API}/services/attorneys/search?${params}`);
      setAttorneys(res.data.attorneys || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegister = async () => {
    setRegistering(true);
    try {
      const payload = {
        ...registerForm,
        bar_admissions: [{
          state: registerForm.primary_state,
          bar_number: registerForm.bar_number,
          status: "active"
        }],
        preferred_courts: registerForm.preferred_courts ? 
          registerForm.preferred_courts.split(",").map(c => ({ court: c.trim(), type: "state" })) : []
      };
      await axios.post(`${API}/services/attorneys/register`, payload);
      setRegistered(true);
      setShowRegister(false);
    } catch (e) {
      console.error(e);
    } finally {
      setRegistering(false);
    }
  };
  
  const togglePracticeArea = (area) => {
    setRegisterForm(prev => ({
      ...prev,
      practice_areas: prev.practice_areas.includes(area) 
        ? prev.practice_areas.filter(a => a !== area)
        : [...prev.practice_areas, area]
    }));
  };
  
  const toggleSkillSet = (skill) => {
    setRegisterForm(prev => ({
      ...prev,
      skill_sets: prev.skill_sets.includes(skill)
        ? prev.skill_sets.filter(s => s !== skill)
        : [...prev.skill_sets, skill]
    }));
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="attorney-network-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-6xl">
        <div className="mb-6">
          <Link to="/tools/services-hub" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Services Hub
          </Link>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Attorney Network</h1>
              <p className="text-muted-foreground">Nationwide bar-admitted contract attorneys for appearances, depositions, and case support</p>
            </div>
            <Button onClick={() => setShowRegister(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Join as Attorney
            </Button>
          </div>
        </div>
        
        {registered && (
          <Alert className="mb-6 bg-emerald-50 border-emerald-200">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800">
              Registration submitted successfully! Your profile is pending review and will be activated within 24-48 hours.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <Label>State</Label>
                <Select value={searchState} onValueChange={setSearchState}>
                  <SelectTrigger><SelectValue placeholder="Any state" /></SelectTrigger>
                  <SelectContent>
                    {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Practice Area</Label>
                <Select value={searchPractice} onValueChange={setSearchPractice}>
                  <SelectTrigger><SelectValue placeholder="Any area" /></SelectTrigger>
                  <SelectContent>
                    {PRACTICE_AREAS.map(p => <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 flex items-end">
                <Button onClick={search} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                  Search Attorneys
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Registration Modal */}
        {showRegister && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Join the Attorney Network</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowRegister(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>Complete your profile to receive case assignments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h3 className="font-semibold mb-3">Basic Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>First Name *</Label>
                      <Input value={registerForm.first_name} onChange={e => setRegisterForm({...registerForm, first_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Last Name *</Label>
                      <Input value={registerForm.last_name} onChange={e => setRegisterForm({...registerForm, last_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" value={registerForm.email} onChange={e => setRegisterForm({...registerForm, email: e.target.value})} />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input value={registerForm.phone} onChange={e => setRegisterForm({...registerForm, phone: e.target.value})} />
                    </div>
                  </div>
                </div>
                
                {/* Bar Admission */}
                <div>
                  <h3 className="font-semibold mb-3">Bar Admission</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Primary State *</Label>
                      <Select value={registerForm.primary_state} onValueChange={v => setRegisterForm({...registerForm, primary_state: v})}>
                        <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                        <SelectContent>
                          {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Bar Number *</Label>
                      <Input value={registerForm.bar_number} onChange={e => setRegisterForm({...registerForm, bar_number: e.target.value})} />
                    </div>
                    <div>
                      <Label>Years of Experience</Label>
                      <Input type="number" value={registerForm.years_experience} onChange={e => setRegisterForm({...registerForm, years_experience: parseInt(e.target.value) || 0})} />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <input type="checkbox" id="federal" checked={registerForm.federal_court_admitted} onChange={e => setRegisterForm({...registerForm, federal_court_admitted: e.target.checked})} className="rounded" />
                    <Label htmlFor="federal">Federal Court Admitted</Label>
                  </div>
                </div>
                
                {/* Practice Areas */}
                <div>
                  <h3 className="font-semibold mb-3">Practice Areas *</h3>
                  <div className="flex flex-wrap gap-2">
                    {PRACTICE_AREAS.map(area => (
                      <Badge 
                        key={area} 
                        variant={registerForm.practice_areas.includes(area) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => togglePracticeArea(area)}
                      >
                        {area.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {/* Skill Sets */}
                <div>
                  <h3 className="font-semibold mb-3">Skill Sets *</h3>
                  <div className="flex flex-wrap gap-2">
                    {SKILL_SETS.map(skill => (
                      <Badge 
                        key={skill} 
                        variant={registerForm.skill_sets.includes(skill) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleSkillSet(skill)}
                      >
                        {skill.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {/* Court Preferences */}
                <div>
                  <h3 className="font-semibold mb-3">Court Preferences</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Home Court</Label>
                      <Input value={registerForm.home_court} onChange={e => setRegisterForm({...registerForm, home_court: e.target.value})} placeholder="e.g., LA Superior Court" />
                    </div>
                    <div>
                      <Label>Preferred Courts (comma separated)</Label>
                      <Input value={registerForm.preferred_courts} onChange={e => setRegisterForm({...registerForm, preferred_courts: e.target.value})} placeholder="e.g., LASC, Orange County, SDNY" />
                    </div>
                  </div>
                </div>
                
                {/* Rates & Availability */}
                <div>
                  <h3 className="font-semibold mb-3">Rates & Availability</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Hourly Rate ($)</Label>
                      <Input type="number" value={registerForm.hourly_rate} onChange={e => setRegisterForm({...registerForm, hourly_rate: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div>
                      <Label>Availability</Label>
                      <Select value={registerForm.availability} onValueChange={v => setRegisterForm({...registerForm, availability: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">Full Time</SelectItem>
                          <SelectItem value="part_time">Part Time</SelectItem>
                          <SelectItem value="project_based">Project Based</SelectItem>
                          <SelectItem value="as_needed">As Needed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Collaboration Style</Label>
                      <Select value={registerForm.collaboration_style} onValueChange={v => setRegisterForm({...registerForm, collaboration_style: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="independent">Independent</SelectItem>
                          <SelectItem value="collaborative">Collaborative</SelectItem>
                          <SelectItem value="either">Either</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                {/* Insurance */}
                <div>
                  <h3 className="font-semibold mb-3">Malpractice Insurance</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="malpractice" checked={registerForm.malpractice_insurance} onChange={e => setRegisterForm({...registerForm, malpractice_insurance: e.target.checked})} className="rounded" />
                      <Label htmlFor="malpractice">I have malpractice insurance</Label>
                    </div>
                    {registerForm.malpractice_insurance && (
                      <div className="flex items-center gap-2">
                        <Label>Coverage Amount: $</Label>
                        <Input type="number" className="w-32" value={registerForm.insurance_amount} onChange={e => setRegisterForm({...registerForm, insurance_amount: parseFloat(e.target.value) || 0})} />
                      </div>
                    )}
                  </div>
                </div>
                
                <Button 
                  onClick={handleRegister} 
                  disabled={registering || !registerForm.first_name || !registerForm.last_name || !registerForm.email || !registerForm.primary_state || !registerForm.bar_number || registerForm.practice_areas.length === 0}
                  className="w-full"
                >
                  {registering ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Submit Registration
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Results */}
        <div className="grid md:grid-cols-2 gap-4">
          {attorneys.map((atty, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold">{atty.first_name} {atty.last_name}</h3>
                    <p className="text-sm text-muted-foreground">{atty.primary_state} • {atty.years_experience} years</p>
                  </div>
                  <Badge>${atty.hourly_rate}/hr</Badge>
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {atty.practice_areas?.slice(0, 3).map((pa, j) => (
                    <Badge key={j} variant="outline" className="text-xs">{pa.replace(/_/g, ' ')}</Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1">Request Availability</Button>
                  <Button size="sm" variant="outline">View Profile</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {attorneys.length === 0 && !loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">Search for attorneys or register to join the network</p>
              <Button onClick={() => setShowRegister(true)}>Join as Contract Attorney</Button>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default AttorneyNetworkPage;
